import { create } from 'zustand';

interface BrowseJobsFiltersStore {
  jobTypeFilters: string[];
  jobCategoryFilters: string[];
  dateFilters: string;
  range: { from: number; to: number };
  selectedCountries: string[];
  selectedCountriesStates: string[];
  selectedExperiences: string[];
  resetCount: number;

  clearAllFilters: () => void;

  addJobType: (jobType: string) => void;
  removeJobType: (jobType: string) => void;
  clearJobTypes: () => void;
  addJobCategory: (jobType: string) => void;
  removeJobCategory: (jobType: string) => void;
  clearJobCategories: () => void;
  addDateFilter: (dateLabel: string) => void;
  removeDateFilter: (dateLabel: string) => void;
  clearDateFilters: () => void;
  setRange: (from: number, to: number) => void;
  resetRange: () => void;
  addCountry: (country: string) => void;
  removeCountry: (country: string) => void;
  clearCountries: () => void;
  addCountryState: (countryState: string) => void;
  removeCountryState: (countryState: string) => void;
  clearCountriesStates: () => void;
  addExperience: (experience: string) => void;
  removeExperience: (experience: string) => void;
  clearExperience: () => void;
}

const useBrowseJobsFiltersStore = create<BrowseJobsFiltersStore>(
  set => ({
    jobTypeFilters: [],
    jobCategoryFilters: [],
    dateFilters: '',
    range: { from: 0, to: 0 },
    selectedCountries: [],
    selectedCountriesStates: [],
    selectedExperiences: [],
    resetCount: 0,

    addJobType: jobType =>
      set(state => ({
        jobTypeFilters: [...state.jobTypeFilters, jobType],
      })),

    removeJobType: jobType =>
      set(state => ({
        jobTypeFilters: state.jobTypeFilters.filter(
          item => item !== jobType
        ),
      })),

    clearJobTypes: () => set({ jobTypeFilters: [] }),

    addJobCategory: jobCategory =>
      set(state => ({
        jobCategoryFilters: [
          ...state.jobCategoryFilters,
          jobCategory,
        ],
      })),

    removeJobCategory: jobCategory =>
      set(state => ({
        jobCategoryFilters: state.jobCategoryFilters.filter(
          item => item !== jobCategory
        ),
      })),

    clearJobCategories: () => set({ jobCategoryFilters: [] }),

    addDateFilter: dateLabel =>
      set(() => ({
        dateFilters: dateLabel,
      })),

    removeDateFilter: dateLabel =>
      set(() => ({
        dateFilters: '',
      })),

    clearDateFilters: () => set({ dateFilters: '' }),

    setRange: (from, to) =>
      set({
        range: { from: Math.max(0, from), to: Math.max(0, to) },
      }),

    resetRange: () => set({ range: { from: 0, to: 0 } }),

    addCountry: country =>
      set(state => ({
        selectedCountries: [...state.selectedCountries, country],
      })),

    removeCountry: country =>
      set(state => ({
        selectedCountries: state.selectedCountries.filter(
          item => item !== country
        ),
      })),

    clearCountries: () => set({ selectedCountries: [] }),

    addCountryState: countryState =>
      set(state => ({
        selectedCountriesStates: [
          ...state.selectedCountriesStates,
          countryState,
        ],
      })),

    removeCountryState: countryState =>
      set(state => ({
        selectedCountriesStates: state.selectedCountriesStates.filter(
          item => item !== countryState
        ),
      })),

    clearCountriesStates: () => set({ selectedCountriesStates: [] }),

    addExperience: experience =>
      set(state => ({
        selectedExperiences: [
          ...state.selectedExperiences,
          experience,
        ],
      })),

    removeExperience: experience =>
      set(state => ({
        selectedExperiences: state.selectedExperiences.filter(
          item => item !== experience
        ),
      })),

    clearExperience: () => set({ selectedExperiences: [] }),

    clearAllFilters: () =>
      set(state => ({
        jobTypeFilters: [],
        dateFilters: '',
        range: { from: 0, to: 0 },
        selectedCountries: [],
        selectedCountriesStates: [],
        selectedExperiences: [],
        resetCount: state.resetCount + 1, // Correct increment of resetCount
      })),
  })
);

export default useBrowseJobsFiltersStore;
